import React from 'react';
import { XIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, UserIcon, CalendarIcon, ClockIcon, LinkIcon, ExternalLinkIcon, BuildingIcon, InfoIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, HashIcon, TagIcon } from 'lucide-react';
import { LedgerEntry, DocumentType, LedgerState } from './LedgerView';
import { formatAmount } from '../utils/currencyFormatter';
interface LedgerEntryDetailProps {
  entry: LedgerEntry;
  onClose: () => void;
  locale: string;
}
export function LedgerEntryDetail({
  entry,
  onClose,
  locale
}: LedgerEntryDetailProps) {
  // Get document type details
  const getDocumentTypeDetails = (type: DocumentType) => {
    switch (type) {
      case 'Invoice':
        return {
          icon: <ReceiptIcon className="h-5 w-5" data-id="element-10350" />,
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
          label: 'Invoice'
        };
      case 'Purchase Order':
        return {
          icon: <ShoppingCartIcon className="h-5 w-5" data-id="element-10351" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          label: 'Purchase Order'
        };
      case 'Credit Note':
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-10352" />,
          color: 'text-orange-600',
          bgColor: 'bg-orange-100',
          label: 'Credit Note'
        };
      case 'Expense Claim':
        return {
          icon: <CreditCardIcon className="h-5 w-5" data-id="element-10353" />,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          label: 'Expense Claim'
        };
      case 'Contract':
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-10354" />,
          color: 'text-indigo-600',
          bgColor: 'bg-indigo-100',
          label: 'Contract'
        };
      case 'Payment':
        return {
          icon: <DollarSignIcon className="h-5 w-5" data-id="element-10355" />,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-100',
          label: 'Payment'
        };
      default:
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-10356" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          label: 'Document'
        };
    }
  };
  const documentDetails = getDocumentTypeDetails(entry.documentType);
  // Get ledger state details
  const getLedgerStateDetails = (state: LedgerState) => {
    switch (state) {
      case 'Informational':
        return {
          icon: <InfoIcon className="h-5 w-5" data-id="element-10357" />,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          label: 'Informational',
          description: 'Draft entry - not yet impacting financial exposure'
        };
      case 'Active':
        return {
          icon: <CheckCircleIcon className="h-5 w-5" data-id="element-10358" />,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          label: 'Active',
          description: 'Active entry impacting financial exposure'
        };
      case 'Reversed':
        return {
          icon: <XCircleIcon className="h-5 w-5" data-id="element-10359" />,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          label: 'Reversed',
          description: 'Entry has been reversed'
        };
      default:
        return {
          icon: <InfoIcon className="h-5 w-5" data-id="element-10360" />,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          label: state,
          description: ''
        };
    }
  };
  const stateDetails = getLedgerStateDetails(entry.ledgerState);
  const isReversal = entry.ledgerEntryType === 'Reversal';
  return <div className="fixed inset-0 overflow-hidden z-20" aria-labelledby="slide-over-title" role="dialog" aria-modal="true" data-id="element-10361">
      <div className="absolute inset-0 overflow-hidden" data-id="element-10362">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-10363"></div>
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-10364">
          <div className="relative w-screen max-w-2xl" data-id="element-10365">
            <div className="h-full flex flex-col py-6 bg-white shadow-xl overflow-y-auto" data-id="element-10366">
              {/* Header */}
              <div className="px-4 sm:px-6" data-id="element-10367">
                <div className="flex items-start justify-between" data-id="element-10368">
                  <h2 className="text-lg font-medium text-gray-900" id="slide-over-title" data-id="element-10369">
                    Operational Ledger Entry
                  </h2>
                  <div className="ml-3 h-7 flex items-center" data-id="element-10370">
                    <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={onClose} data-id="element-10371">
                      <span className="sr-only" data-id="element-10372">Close panel</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-10373" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="mt-6 relative flex-1 px-4 sm:px-6" data-id="element-10374">
                {/* Entry ID and State */}
                <div className="border-b border-gray-200 pb-5 mb-5" data-id="element-10375">
                  <div className="flex justify-between items-start" data-id="element-10376">
                    <div data-id="element-10377">
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-10378">
                        {entry.documentNumber}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 font-mono" data-id="element-10379">
                        Entry ID: {entry.ledgerEntryId}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stateDetails.bgColor} ${stateDetails.textColor}`} data-id="element-10380">
                      {stateDetails.icon}
                      <span className="ml-1" data-id="element-10381">{stateDetails.label}</span>
                    </span>
                  </div>
                  {stateDetails.description && <p className="mt-2 text-sm text-gray-600" data-id="element-10382">
                      {stateDetails.description}
                    </p>}
                </div>

                {/* Reversal Warning */}
                {isReversal && entry.reversesLedgerEntryId && <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4" data-id="element-10383">
                    <div className="flex" data-id="element-10384">
                      <AlertCircleIcon className="h-5 w-5 text-orange-400" data-id="element-10385" />
                      <div className="ml-3" data-id="element-10386">
                        <h3 className="text-sm font-medium text-orange-800" data-id="element-10387">
                          Reversal Entry
                        </h3>
                        <p className="mt-1 text-sm text-orange-700" data-id="element-10388">
                          This entry reverses ledger entry:{' '}
                          <span className="font-mono" data-id="element-10389">
                            {entry.reversesLedgerEntryId}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>}

                {/* Document information */}
                <div className="mb-6" data-id="element-10390">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10391">
                    DOCUMENT INFORMATION
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-10392">
                    <div className="flex items-center mb-3" data-id="element-10393">
                      <div className={`p-2 rounded-md ${documentDetails.bgColor} ${documentDetails.color} mr-3`} data-id="element-10394">
                        {documentDetails.icon}
                      </div>
                      <div data-id="element-10395">
                        <p className="text-sm font-medium text-gray-900" data-id="element-10396">
                          {documentDetails.label}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-10397">
                          Status: {entry.documentStatus}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-10398">
                      <div data-id="element-10399">
                        <p className="text-xs text-gray-500" data-id="element-10400">Document ID</p>
                        <p className="font-mono text-gray-900" data-id="element-10401">
                          {entry.documentId}
                        </p>
                      </div>
                      <div data-id="element-10402">
                        <p className="text-xs text-gray-500" data-id="element-10403">Entry Type</p>
                        <p className="text-gray-900" data-id="element-10404">{entry.ledgerEntryType}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center" data-id="element-10405">
                      <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10406" />
                      <a href="#" className="text-sm text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-10407">
                        View Document{' '}
                        <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-10408" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Organisational Context */}
                <div className="mb-6" data-id="element-10409">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10410">
                    ORGANISATIONAL CONTEXT
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-10411">
                    <div className="flex items-start mb-2" data-id="element-10412">
                      <BuildingIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" data-id="element-10413" />
                      <div className="flex-1" data-id="element-10414">
                        <p className="text-xs text-gray-500" data-id="element-10415">Org Unit</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-10416">
                          {entry.orgUnitName}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-10417">
                          Path: {entry.orgUnitPath}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                <div className="mb-6" data-id="element-10418">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10419">
                    FINANCIAL IMPACT
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-10420">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-10421">
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200" data-id="element-10422">
                        <p className="text-xs text-green-600 font-medium mb-1" data-id="element-10423">
                          Debit (Increases Exposure)
                        </p>
                        <p className="text-xl font-mono font-bold text-green-700" data-id="element-10424">
                          {entry.debitAmount ? formatAmount(entry.debitAmount, locale) : '-'}
                        </p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 border border-red-200" data-id="element-10425">
                        <p className="text-xs text-red-600 font-medium mb-1" data-id="element-10426">
                          Credit (Reduces Exposure)
                        </p>
                        <p className="text-xl font-mono font-bold text-red-700" data-id="element-10427">
                          {entry.creditAmount ? formatAmount(entry.creditAmount, locale) : '-'}
                        </p>
                      </div>
                    </div>
                    {entry.calculatedRunningBalance !== undefined && <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200" data-id="element-10428">
                        <p className="text-xs text-indigo-600 font-medium mb-1" data-id="element-10429">
                          Running Balance (Calculated)
                        </p>
                        <p className="text-xl font-mono font-bold text-indigo-700" data-id="element-10430">
                          {formatAmount(entry.calculatedRunningBalance, locale)}
                        </p>
                        <p className="text-xs text-indigo-600 mt-1" data-id="element-10431">
                          Display Precision: {entry.displayPrecision} decimals
                        </p>
                      </div>}
                  </div>
                </div>

                {/* GL Coding */}
                <div className="mb-6" data-id="element-10432">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10433">
                    GL CODING & ALLOCATION
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-10434">
                    <div className="flex items-center" data-id="element-10435">
                      <HashIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10436" />
                      <div data-id="element-10437">
                        <p className="text-xs text-gray-500" data-id="element-10438">GL Code</p>
                        <p className="text-sm font-mono font-medium text-gray-900" data-id="element-10439">
                          {entry.glCode}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Counterparty */}
                {(entry.vendorName || entry.userName) && <div className="mb-6" data-id="element-10440">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10441">
                      COUNTERPARTY
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-10442">
                      <div className="flex items-center" data-id="element-10443">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10444" />
                        <div data-id="element-10445">
                          <p className="text-xs text-gray-500" data-id="element-10446">
                            {entry.vendorName ? 'Vendor' : 'User'}
                          </p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-10447">
                            {entry.vendorName || entry.userName}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Audit and Traceability */}
                <div className="mb-6" data-id="element-10448">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10449">
                    AUDIT & TRACEABILITY
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-id="element-10450">
                    <div className="flex items-center" data-id="element-10451">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10452" />
                      <div data-id="element-10453">
                        <p className="text-xs text-gray-500" data-id="element-10454">Created Date</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-10455">
                          {new Date(entry.createdDate).toLocaleString(locale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-10456">
                      <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10457" />
                      <div data-id="element-10458">
                        <p className="text-xs text-gray-500" data-id="element-10459">Created By</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-10460">
                          {entry.createdBy}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-10461">
                      <TagIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10462" />
                      <div data-id="element-10463">
                        <p className="text-xs text-gray-500" data-id="element-10464">Source System</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-10465">
                          {entry.sourceSystem}
                        </p>
                      </div>
                    </div>
                    {entry.erpReference && <div className="flex items-center" data-id="element-10466">
                        <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10467" />
                        <div data-id="element-10468">
                          <p className="text-xs text-gray-500" data-id="element-10469">ERP Reference</p>
                          <p className="text-sm font-mono font-medium text-gray-900" data-id="element-10470">
                            {entry.erpReference}
                          </p>
                        </div>
                      </div>}
                  </div>
                </div>

                {/* Notes */}
                {entry.reasonOrNotes && <div className="mb-6" data-id="element-10471">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10472">
                      REASON / NOTES
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-10473">
                      <p className="text-sm text-gray-900" data-id="element-10474">
                        {entry.reasonOrNotes}
                      </p>
                    </div>
                  </div>}

                {/* Read-only Notice */}
                <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4" data-id="element-10475">
                  <div className="flex" data-id="element-10476">
                    <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-10477" />
                    <div className="ml-3" data-id="element-10478">
                      <p className="text-sm text-blue-700" data-id="element-10479">
                        <strong data-id="element-10480">Read-Only Ledger:</strong> This entry is
                        append-only and cannot be modified. Corrections are made
                        through reversing entries.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}